<script setup>
  import { computed, inject, ref } from "vue";
  import { ChevronDown, ChevronUp, FileText, MessageSquare, Plus } from "lucide-vue-next";
  import { storeToRefs } from "pinia";
  import { Button } from "@/components/ui/button";
  import { Spinner } from "@/components/ui/spinner";
  import { EmptyState } from "@/components/episto-ui/empty-state";
  import AiStar from "@/components/episto-ui/ai/AiStar.vue";
  import CommentsStatusSwitch from "./CommentsStatusSwitch.vue";
  import NewThreadComposer from "./NewThreadComposer.vue";
  import RightPanel from "./RightPanel.vue";
  import ReviewThreadCard from "./ReviewThreadCard.vue";
  import { useAssistantStore } from "@/stores/assistant";
  import { SURVEY_GROUP, useReviewCommentsStore } from "@stores/survey/reviewComments";
  import { useSurveyCardsStore } from "@stores/survey/surveyCards";
  import { useSurveyStore } from "@stores/survey/survey";
  import { useCardAttributes } from "@utils/composables/questionCard";
  import { removeHtmlMarkdownAndLineBreak } from "@utils/toolBox";

  const store = useReviewCommentsStore();
  const { panel, threads } = storeToRefs(store);
  const { isDraft } = storeToRefs(useSurveyStore());
  const { isStreaming } = storeToRefs(useAssistantStore());
  const cardsStore = useSurveyCardsStore();
  const { cardsTitle } = useCardAttributes();
  const reviewEventBus = inject("reviewEventBus");

  const list = ref(null);
  const scrollToOpenGroup = () =>
    list.value
      ?.querySelector(`[data-group="${panel.value.openCard}"]`)
      ?.scrollIntoView({ behavior: "smooth", block: "start" });

  const cardOf = (token) => cardsStore.findCardByToken?.({ token });
  const excerptOf = (card) => (card ? removeHtmlMarkdownAndLineBreak(cardsTitle(card)) : "");
  const surveyOpen = computed(() => panel.value.openCard === SURVEY_GROUP);
  const surveyComposing = ref(false);
  const composerTarget = computed(() =>
    surveyOpen.value
      ? surveyComposing.value && "the survey"
      : panel.value.openCard && cardOf(panel.value.openCard)?.name,
  );
  const toggleSurvey = () => {
    surveyComposing.value = false;
    store.toggleCard(SURVEY_GROUP);
  };
  const commentOnSurvey = () => {
    surveyComposing.value = true;
    store.openCardComments(SURVEY_GROUP);
  };

  const cardPositions = computed(() => new Map((cardsStore.cards ?? []).map((card, index) => [card.token, index])));

  const panelThreads = computed(() => threads.value.filter(store.matchesPanel));
  const surveyThreads = computed(() => panelThreads.value.filter((thread) => thread.card_token === null));
  const showSurvey = computed(() => panel.value.status === "open" || surveyThreads.value.length);
  const canAddSurveyComment = computed(() => panel.value.status === "open" && surveyThreads.value.length > 0);

  const groups = computed(() => {
    const byCard = new Map();
    panelThreads.value.forEach((thread) => {
      if (thread.card_token === null) return;
      if (!byCard.has(thread.card_token)) byCard.set(thread.card_token, []);
      byCard.get(thread.card_token).push(thread);
    });
    return [...byCard.entries()]
      .map(([token, list]) => {
        const card = cardOf(token);
        return {
          token,
          name: card?.name ?? token,
          excerpt: excerptOf(card),
          position: cardPositions.value.get(token) ?? Infinity,
          threads: list,
        };
      })
      .sort((groupA, groupB) => groupA.position - groupB.position);
  });

  const toggleGroup = (group) => {
    const opening = panel.value.openCard !== group.token;
    store.toggleCard(group.token);
    if (opening) reviewEventBus?.emit({ command: "select", token: group.token });
  };

  const submitComposer = (body, mentions) => {
    store.createThread(surveyOpen.value ? null : panel.value.openCard, body, mentions);
  };
</script>

<template>
  <RightPanel
    class="h-full"
    :panes="['comments']"
    data-cy="review-comments-panel">
    <CommentsStatusSwitch
      v-model="panel.status"
      class="mx-3 mt-3" />

    <div
      ref="list"
      class="flex min-h-0 flex-1 flex-col gap-1.5 overflow-y-auto px-3 py-3">
      <section
        v-if="showSurvey"
        :data-group="SURVEY_GROUP"
        class="flex flex-col gap-2 rounded-xl border border-episto-navy-100 bg-episto-navy-50"
        :class="surveyOpen ? 'p-1.5' : 'px-1.5'"
        aria-label="Comments on the survey"
        data-cy="survey-comments">
        <div class="relative">
          <button
            type="button"
            :aria-expanded="surveyOpen"
            class="flex h-9 w-full items-center gap-2 rounded-lg px-1 pr-1 text-left"
            aria-label="Toggle survey comments"
            @click="toggleSurvey">
            <FileText class="size-4 shrink-0 text-primary" />
            <span class="text-13 font-semibold text-primary">Survey</span>
            <span class="min-w-0 flex-1 truncate text-xs text-muted-foreground">
              <template v-if="surveyOpen">General comments</template>
            </span>
            <span
              v-if="!surveyOpen && surveyThreads.length"
              class="inline-flex items-center gap-1 text-xs text-muted-foreground">
              <MessageSquare class="size-3" />{{ surveyThreads.length }}
            </span>
            <span
              v-if="canAddSurveyComment"
              class="w-7 shrink-0"
              :class="surveyOpen && 'w-28'" />
            <component
              :is="surveyOpen ? ChevronUp : ChevronDown"
              class="size-3.5 shrink-0 text-muted-foreground" />
          </button>
          <Button
            v-if="canAddSurveyComment"
            variant="outline"
            size="sm"
            class="absolute top-1/2 right-6 h-7 -translate-y-1/2 gap-1 border-episto-navy-100 px-2 text-xs"
            :class="!surveyOpen && 'w-7 px-0'"
            aria-label="Add a comment on the survey"
            @click="commentOnSurvey">
            <Plus class="size-3.5" /><template v-if="surveyOpen">Add comment</template>
          </Button>
        </div>
        <template v-if="surveyOpen">
          <ReviewThreadCard
            v-for="thread in surveyThreads"
            :key="thread.id"
            class="bg-card!"
            :thread="thread" />
          <EmptyState
            v-if="!surveyThreads.length"
            size="sm"
            title="No comment on the survey yet">
            <template #action>
              <Button
                variant="outline"
                size="sm"
                class="gap-1.5"
                @click="commentOnSurvey">
                <Plus class="size-3.5" />Comment on the survey
              </Button>
            </template>
          </EmptyState>
        </template>
      </section>

      <div
        v-for="group in groups"
        :key="group.token"
        :data-group="group.token"
        class="flex flex-col gap-2">
        <button
          type="button"
          :aria-expanded="panel.openCard === group.token"
          class="flex h-9 w-full items-center gap-2 rounded-lg px-2.5 text-left"
          :class="panel.openCard === group.token ? 'hover:bg-muted' : 'bg-muted hover:bg-secondary'"
          :aria-label="`Toggle ${group.name} comments`"
          @click="toggleGroup(group)">
          <span class="shrink-0 truncate text-13 font-semibold text-primary">{{ group.name }}</span>
          <span class="min-w-0 flex-1 truncate text-xs text-muted-foreground">
            <template v-if="panel.openCard === group.token">{{ group.excerpt }}</template>
          </span>
          <span
            v-if="panel.openCard !== group.token"
            class="inline-flex items-center gap-1 text-xs text-muted-foreground">
            <MessageSquare class="size-3" />{{ group.threads.length }}
          </span>
          <component
            :is="panel.openCard === group.token ? ChevronUp : ChevronDown"
            class="size-3.5 shrink-0 text-muted-foreground" />
        </button>
        <template v-if="panel.openCard === group.token">
          <ReviewThreadCard
            v-for="thread in group.threads"
            :key="thread.id"
            :thread="thread" />
        </template>
      </div>

      <EmptyState
        v-if="!groups.length && panel.status === 'open'"
        class="flex flex-1 flex-col justify-center px-3"
        :icon="MessageSquare"
        title="No comments on cards yet"
        data-cy="review-no-comments">
        <template #action>
          <Button
            v-if="isDraft"
            class="gap-1.5"
            :disabled="isStreaming"
            data-cy="review-with-ai"
            @click="store.requestAiReview()">
            <Spinner
              v-if="isStreaming"
              class="size-4" />
            <AiStar
              v-else
              class="size-4" />
            {{ isStreaming ? "Episto AI is reviewing…" : "Review with Episto AI" }}
          </Button>
        </template>
      </EmptyState>
      <EmptyState
        v-else-if="!groups.length && !surveyThreads.length"
        class="pt-10"
        title="Nothing resolved yet"
        description="Resolved threads will show up here." />
    </div>

    <NewThreadComposer
      v-if="composerTarget && panel.status === 'open'"
      :target="composerTarget"
      :label="surveyOpen ? 'General comment' : null"
      @submit="submitComposer"
      @focus="scrollToOpenGroup" />
  </RightPanel>
</template>
